/**
 * Conteúdo — a pesquisa do que está publicado, e o cadastro.
 *
 * É a tela mais movimentada do sistema antigo: 44.497 registros na listagem sem
 * filtro. Mas esse número engana, e a tela não deixa ele enganar ninguém: a
 * `TabelaPesquisa` guarda **uma linha por idioma**, então sem filtrar idioma
 * cada conteúdo aparece duas vezes. Por isso o idioma é um filtro em pé de
 * igualdade com os outros e vira coluna na grade.
 *
 * **Isto é dado global.** Não pertence a projeto, então a trava do canário
 * recusa toda gravação em desenvolvimento — e o aviso no topo diz isso antes de
 * alguém preencher um formulário inteiro.
 *
 * **A lista vem do último incluído ou alterado** (a API ordena, então a paginação
 * mantém a ordem), e toda ação que dá certo diz isso em verde e realça o conteúdo
 * mexido — que, por ser o mais recente, está no topo da página 1.
 */

import { useMemo, useRef, useState } from "react"
import {
  usarConsolidacaoPendente, usarConteudos, usarExportarConteudo, usarSolicitarConsolidacao,
  siglaDoIdioma, IDIOMAS,
  type ConteudoNaLista, type FiltroDeConteudo,
} from "@/api/conteudo"
import { filtrarPelaMarca, usarCatalogosDeConteudo, type OpcaoDeCatalogo } from "@/api/catalogosDeConteudo"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import { usarRecemIncluido, usarRolarAteRecente } from "@/kit/ordemDaLista"
import { FichaDoConteudo } from "./FichaDoConteudo"
import { IncluirConteudo } from "./IncluirConteudo"

const POR_PAGINA = 50

/** Uma ação que deu certo: a frase, e os conteúdos que ela mexeu (para realçar). */
export type Concluir = (mensagem: string, dadoIds?: number[]) => void

export function Conteudo() {
  const [filtro, setFiltro] = useState<FiltroDeConteudo>({ baseId: 1 })
  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<{ dadoId: number; baseId: number } | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [deuCerto, setDeuCerto] = useState(false)
  const recente = usarRecemIncluido()
  const rolagem = useRef<HTMLDivElement>(null)

  // Erro, aviso, ou nada: a barra volta ao normal.
  const avisar = (m: string | null) => { setDeuCerto(false); setRecado(m) }
  // Deu certo: frase em verde e, se mexeu em conteúdo, ele realçado no topo da página 1.
  const concluir: Concluir = (m, dadoIds = []) => {
    setDeuCerto(true)
    setRecado(m)
    if (dadoIds.length > 0) { recente.marcar(dadoIds); setPagina(1) }
  }

  const catalogos = usarCatalogosDeConteudo()
  const lista = usarConteudos(filtro, pagina, POR_PAGINA)
  const consolidacao = usarConsolidacaoPendente()
  const pedirConsolidacao = usarSolicitarConsolidacao()
  const exportar = usarExportarConteudo()

  function mudar<C extends keyof FiltroDeConteudo>(campo: C, valor: FiltroDeConteudo[C]) {
    setFiltro((f) => {
      const novo = { ...f, [campo]: valor }
      // Trocar a marca invalida o veículo e o motor escolhidos: eles são de
      // outra marca, e deixar o valor antigo devolveria lista vazia sem dizer
      // por quê.
      if (campo === "marcaId") { delete novo.veiculoId; delete novo.motorId }
      return novo
    })
    setPagina(1)
  }

  const veiculos = filtrarPelaMarca(catalogos.veiculos.itens, filtro.marcaId ?? 0)
  const motores = filtrarPelaMarca(catalogos.motores.itens, filtro.marcaId ?? 0)

  const quantosFiltros = useMemo(
    () => Object.entries(filtro).filter(([k, v]) =>
      k !== "baseId" && k !== "ordem" && typeof v === "number" && v > 0).length,
    [filtro])

  const total = lista.data?.totalItens ?? 0
  const totalPaginas = lista.data?.totalPaginas ?? 0
  usarRolarAteRecente(rolagem, recente.id, lista.data)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Conteúdo"
        detalhe={lista.data ? `${total.toLocaleString("pt-BR")} no recorte atual` : undefined}
      >
        {consolidacao.data?.pendente && (
          <span
            title="A busca do site ainda não reflete as últimas alterações."
            style={{
              fontSize: 11, padding: "3px 8px", borderRadius: 4,
              background: "var(--aviso-soft, #fdf1d8)", color: "var(--aviso-on-soft, #7a4b00)",
            }}
          >
            consolidação pendente
          </span>
        )}

        <Botao
          aoClicar={() => {
            avisar(null)
            pedirConsolidacao.mutate(undefined, {
              onSuccess: (r) => concluir(r.mensagem || "Consolidação solicitada."),
              onError: (e) => avisar(mensagem(e)),
            })
          }}
          ocupado={pedirConsolidacao.isPending}
        >
          Consolidar busca
        </Botao>

        <Botao
          aoClicar={() => {
            avisar(null)
            exportar.mutate({ filtro, baseId: filtro.baseId ?? 1 }, {
              onSuccess: (nome) => concluir(`Baixado: ${nome}`),
              onError: (e) => avisar(mensagem(e)),
            })
          }}
          ocupado={exportar.isPending}
        >
          {exportar.isPending ? "Gerando…" : "Exportar CSV"}
        </Botao>

        <Botao aoClicar={() => setIncluindo(true)} destaque>Incluir</Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="O conteúdo vale para todos os projetos e é o que o cliente vê no site" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* filtros */}
        <div style={{
          flex: "0 0 268px", width: 268, overflowY: "auto", padding: 14,
          borderRight: "1px solid var(--line2)",
        }}>
          <Campo rotulo="Idioma">
            <select
              value={filtro.baseId ?? 1}
              onChange={(e) => mudar("baseId", Number(e.target.value))}
              style={estiloCampo}
            >
              {IDIOMAS.map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </Campo>

          <Seletor rotulo="Marca" itens={catalogos.marcas.itens}
                   valor={filtro.marcaId ?? 0} aoMudar={(v) => mudar("marcaId", v)} />
          <Seletor rotulo="Veículo" itens={veiculos}
                   valor={filtro.veiculoId ?? 0} aoMudar={(v) => mudar("veiculoId", v)}
                   nota={filtro.marcaId ? `só os da marca escolhida (${veiculos.length})` : undefined} />
          <Seletor rotulo="Motor" itens={motores}
                   valor={filtro.motorId ?? 0} aoMudar={(v) => mudar("motorId", v)}
                   nota={filtro.marcaId ? `só os da marca escolhida (${motores.length})` : undefined} />
          <Seletor rotulo="Assunto" itens={catalogos.assuntos.itens}
                   valor={filtro.assuntoId ?? 0} aoMudar={(v) => mudar("assuntoId", v)} />
          <Seletor rotulo="Sistema" itens={catalogos.sistemas.itens}
                   valor={filtro.sistemaId ?? 0} aoMudar={(v) => mudar("sistemaId", v)} />
          <Seletor rotulo="Nome de sistema" itens={catalogos.nomes.itens}
                   valor={filtro.nomeSistemaId ?? 0} aoMudar={(v) => mudar("nomeSistemaId", v)} />
          <Seletor rotulo="Fabricante" itens={catalogos.fabricantes.itens}
                   valor={filtro.fabSistemaId ?? 0} aoMudar={(v) => mudar("fabSistemaId", v)} />
          <Seletor rotulo="Produto" itens={catalogos.produtos.itens}
                   valor={filtro.produtoId ?? 0} aoMudar={(v) => mudar("produtoId", v)} />
          <Seletor rotulo="Origem" itens={catalogos.origens.itens}
                   valor={filtro.origemId ?? 0} aoMudar={(v) => mudar("origemId", v)} />

          {quantosFiltros > 0 && (
            <button
              onClick={() => { setFiltro({ baseId: filtro.baseId ?? 1 }); setPagina(1) }}
              style={{
                marginTop: 10, border: 0, background: "transparent", color: "var(--acao)",
                cursor: "pointer", fontSize: 11.5, padding: "4px 0",
              }}
            >
              limpar os {quantosFiltros} filtros
            </button>
          )}
        </div>

        {/* grade */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div ref={rolagem} style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {lista.isPending ? (
              <p style={{ padding: 16, fontSize: 12.5, color: "var(--txt2)" }}>Procurando…</p>
            ) : lista.error ? (
              <p style={{ padding: 16, fontSize: 12.5, color: "var(--erro)" }}>
                {lista.error instanceof ErroDaApi
                  ? lista.error.message : "Não foi possível pesquisar o conteúdo."}
              </p>
            ) : total === 0 ? (
              <p style={{ padding: 16, fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6, maxWidth: "56ch" }}>
                <b>Nenhum conteúdo com essa combinação.</b><br />
                Os filtros não se reduzem entre si — dá para escolher uma combinação que não
                existe, e é isto que ela parece.
              </p>
            ) : (
              <Grade
                itens={lista.data?.itens ?? []}
                selecionado={aberto}
                recente={recente.tem}
                aoAbrir={(c) => setAberto({ dadoId: c.dadoId, baseId: c.baseId })}
              />
            )}
          </div>

          {totalPaginas > 1 && (
            <Paginacao pagina={pagina} totalPaginas={totalPaginas} aoIr={setPagina} />
          )}
        </div>

        {aberto && (
          <FichaDoConteudo
            dadoId={aberto.dadoId}
            baseId={aberto.baseId}
            aoFechar={() => setAberto(null)}
            aoAvisar={avisar}
            aoConcluir={concluir}
          />
        )}
      </div>

      {incluindo && (
        <IncluirConteudo
          aoFechar={() => setIncluindo(false)}
          aoAvisar={avisar}
          aoConcluir={concluir}
          idiomaAtual={filtro.baseId ?? 1}
        />
      )}

      <BarraDeEstado
        direita={
          lista.data
            ? `página ${lista.data.paginaAtual} de ${totalPaginas || 1} · ${POR_PAGINA} por página`
            : undefined
        }
      >
        {recado && deuCerto ? (
          <span role="status" style={{ color: "var(--ok-on-soft)", fontWeight: 600 }}>✓ {recado}</span>
        ) : recado ?? (catalogos.carregando ? "Carregando os catálogos…" : `Idioma: ${
          IDIOMAS.find((i) => i.id === (filtro.baseId ?? 1))?.nome}`)}
      </BarraDeEstado>
    </div>
  )
}

// ---------------------------------------------------------------- grade

function Grade({ itens, selecionado, recente, aoAbrir }: {
  itens: ConteudoNaLista[]
  selecionado: { dadoId: number; baseId: number } | null
  /** Os conteúdos que a última ação mexeu: realce que esmaece. */
  recente: (dadoId: number) => boolean
  aoAbrir: (c: ConteudoNaLista) => void
}) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr style={{ position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
          {["Id", "", "Descrição", "Marca", "Veículo", "Motor", "Assunto", "Sistema", "Anos", "Atualizado"].map((c, i) => (
            <th key={i} style={{
              textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600,
              color: "var(--txt2)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
            }}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {itens.map((c) => {
          const ativo = selecionado?.dadoId === c.dadoId && selecionado?.baseId === c.baseId
          return (
            <tr
              key={`${c.dadoId}-${c.baseId}`}
              data-recem-incluido={recente(c.dadoId) ? "" : undefined}
              onClick={() => aoAbrir(c)}
              style={{
                cursor: "pointer",
                background: ativo ? "var(--acao-soft)" : undefined,
                borderBottom: "1px solid var(--line2)",
              }}
            >
              <td className="mono" style={{ ...cel, whiteSpace: "nowrap" }}>{c.dadoId}</td>
              <td style={{ ...cel, whiteSpace: "nowrap" }}>
                <span style={{
                  fontSize: 9.5, padding: "1px 4px", borderRadius: 3,
                  background: "var(--surface2)", color: "var(--txt2)",
                }}>
                  {siglaDoIdioma(c.baseId)}
                </span>
              </td>
              <td style={{ ...cel, maxWidth: 340 }} title={c.dadoDesc}>
                <span style={corte}>{c.dadoDesc}</span>
              </td>
              <td style={cel}>{c.marca}</td>
              <td style={cel}>{c.veiculo}</td>
              <td style={{ ...cel, maxWidth: 160 }} title={c.motor}><span style={corte}>{c.motor}</span></td>
              <td style={{ ...cel, maxWidth: 160 }} title={c.assunto}><span style={corte}>{c.assunto}</span></td>
              <td style={{ ...cel, maxWidth: 160 }} title={c.sistema}><span style={corte}>{c.sistema}</span></td>
              <td style={{ ...cel, whiteSpace: "nowrap" }}>{anos(c.anoIni, c.anoFim)}</td>
              <td style={{ ...cel, whiteSpace: "nowrap", color: "var(--txt2)" }}>{quando(c.data)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/** A data da última inclusão ou alteração, curta: é a ordem da lista. */
function quando(iso?: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
}

/** Ano 0 é "sem data" no cadastro antigo — mostrar "0" confundiria. */
function anos(ini: number, fim: number): string {
  if (!ini && !fim) return "—"
  return `${ini || "?"}–${fim || "?"}`
}

const cel: React.CSSProperties = { padding: "5px 8px", verticalAlign: "top" }
const corte: React.CSSProperties = {
  display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
}

// ---------------------------------------------------------------- paginação

function Paginacao({ pagina, totalPaginas, aoIr }: {
  pagina: number
  totalPaginas: number
  aoIr: (p: number) => void
}) {
  return (
    <div style={{
      flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8, padding: "7px 14px",
      borderTop: "1px solid var(--line2)",
    }}>
      <Botao aoClicar={() => aoIr(Math.max(1, pagina - 1))} desligado={pagina <= 1}>anterior</Botao>
      <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
        {pagina} de {totalPaginas}
      </span>
      <Botao aoClicar={() => aoIr(Math.min(totalPaginas, pagina + 1))} desligado={pagina >= totalPaginas}>
        próxima
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------- peças

function Campo({ rotulo, children, nota }: {
  rotulo: string
  children: React.ReactNode
  nota?: string
}) {
  return (
    <div style={{ marginBottom: 9 }}>
      <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
        {rotulo}
      </label>
      {children}
      {nota && <p style={{ margin: "2px 0 0", fontSize: 10, color: "var(--txt3)" }}>{nota}</p>}
    </div>
  )
}

const estiloCampo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}

/**
 * Um catálogo para escolher: clicar abre a lista, digitar filtra. A primeira
 * opção, "Todos", volta o valor para 0 — sem filtro nessa dimensão.
 */
export function Seletor({ rotulo, itens, valor, aoMudar, nota }: {
  rotulo: string
  itens: OpcaoDeCatalogo[]
  valor: number
  aoMudar: (id: number) => void
  nota?: string
}) {
  return (
    <Campo rotulo={rotulo} nota={nota}>
      <SeletorComBusca itens={itens} valor={valor} aoMudar={aoMudar}
                       rotuloVazio="Todos" rotuloAcessivel={rotulo} />
    </Campo>
  )
}

export function Botao({ children, aoClicar, destaque, desligado, ocupado }: {
  children: React.ReactNode
  aoClicar: () => void
  destaque?: boolean
  desligado?: boolean
  ocupado?: boolean
}) {
  const inativo = desligado || ocupado
  return (
    <button
      onClick={aoClicar}
      disabled={inativo}
      style={{
        border: "1px solid " + (destaque && !inativo ? "var(--acao)" : "var(--line)"),
        borderRadius: 6, padding: "4px 10px", fontSize: 12,
        cursor: inativo ? "default" : "pointer",
        background: destaque && !inativo ? "var(--acao)" : "var(--surface)",
        color: destaque && !inativo ? "#fff" : inativo ? "var(--txt3)" : "var(--txt)",
      }}
    >
      {children}
    </button>
  )
}

/** A trava explica o que fazer; resumir a mensagem dela só atrapalha. */
export function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return "Não foi possível concluir."
}
